"use client";

import { useState, useEffect } from "react";
import { X, ExternalLink, Check } from "lucide-react";
import { authService } from "@/services/auth.service";

interface TermsConsentModalProps {
  open: boolean;
  onClose: () => void;
  onAgree: (consents: {
    serviceTerms: boolean;
    privacyPolicy: boolean;
    marketingConsent: boolean;
  }) => void;
  userCode?: string;
  userEmail?: string;
}

export default function TermsConsentModal({ 
  open, 
  onClose, 
  onAgree, 
  userCode,
  userEmail
}: TermsConsentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showRejectSuccess, setShowRejectSuccess] = useState(false);
  const [consents, setConsents] = useState({
    serviceTerms: false,
    privacyPolicy: false,
    marketingConsent: false,
  });
  const [showDetails, setShowDetails] = useState({
    serviceTerms: false,
    privacyPolicy: false,
    marketingConsent: false,
  });

  // 필수 동의 항목들 (서비스 이용약관, 개인정보 처리방침은 필수)
  const requiredConsents = ['serviceTerms', 'privacyPolicy'];
  const isRequiredConsentsComplete = requiredConsents.every(key => consents[key as keyof typeof consents]);

  const handleAgree = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // props로 전달된 userCode 사용, 없으면 sessionStorage에서 가져오기
      const pendingUserCode = userCode || (typeof window !== 'undefined' ? window.sessionStorage.getItem('pendingUserIdx') : null);
      
      // console.log('[TermsConsentModal] Using userCode:', pendingUserCode, 'from:', userCode ? 'props' : 'sessionStorage');

      if (!pendingUserCode) {
        console.error('[TermsConsentModal] Pending user code not found');
        onClose();
        return;
      }

      // 약관 동의 API 호출
      const data = await authService.termsConsent({ 
        userCode: String(pendingUserCode),
        consents: {
          serviceTerms: consents.serviceTerms,
          privacyPolicy: consents.privacyPolicy,
          marketingConsent: consents.marketingConsent
        }
      });
      if (data.code === "SUCCESS") {
        // console.log('[TermsConsentModal] Terms consent successful');
        onAgree(consents);
        onClose();
      } else {
        console.error('[TermsConsentModal] Terms consent failed:', data.details?.reason);
        // 에러 처리 로직 추가 가능
      }
    } catch (error) {
      console.error('Terms consent error:', error);
      // 에러 처리 로직 추가 가능
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsentChange = (type: keyof typeof consents) => {
    setConsents(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleRejectSuccessConfirm = () => {
    // 수신거부 성공 모달 확인 시 세션 정리 및 리다이렉트
    if (typeof window !== 'undefined') {
      // 세션 스토리지 정리
      sessionStorage.removeItem('activateFlow');
      sessionStorage.removeItem('pendingUserCode');
      
      // 로컬 스토리지 정리 (인증 관련 데이터)
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_info');
      
      // 페이지 리다이렉트 (메인 페이지로)
      window.location.href = '/';
    }
  };

  const handleRejectConsent = () => {
    // 수신거부 확인 모달 표시
    setShowRejectConfirm(true);
  };

  const handleRejectConfirm = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setShowRejectConfirm(false); // 확인 모달 닫기
    
    try {
      // console.log('[TermsConsentModal] Props - userCode:', userCode, 'userEmail:', userEmail);
      
      // 쿠폰번호(사용자 코드)로 수신거부 처리
      const pendingUserCode = userCode || (typeof window !== 'undefined' ? window.sessionStorage.getItem('pendingUserCode') : null);
      // console.log('[TermsConsentModal] Code check - userCode:', userCode, 'sessionStorage:', typeof window !== 'undefined' ? window.sessionStorage.getItem('pendingUserCode') : 'N/A', 'pendingUserCode:', pendingUserCode);
      
      if (!pendingUserCode) {
        console.error('[TermsConsentModal] No user code available for reject consent');
        alert('사용자 정보를 찾을 수 없습니다. 다시 시도해주세요.');
        return;
      }
      
      // console.log('[TermsConsentModal] Using user code for reject consent:', pendingUserCode);
      const result = await authService.rejectConsentByCode(pendingUserCode);
      
      // console.log('[TermsConsentModal] Reject consent result:', result);
      
      if (result && result.code === 'SUCCESS') {
        // 수신거부 성공 시 모달 표시
        setShowRejectSuccess(true);
      } else {
        console.error('수신거부 실패:', result?.message || 'Unknown error');
        alert(`수신거부 처리 중 오류가 발생했습니다: ${result?.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('수신거부 API 호출 오류:', error);
      alert('수신거부 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDetails = (type: keyof typeof showDetails) => {
    setShowDetails(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (open) window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  // 수신거부 확인 모달
  if (showRejectConfirm) {
    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">수신거부 확인</h3>
            <p className="text-gray-600 mb-6">
              정말로 수신거부를 하시겠습니까?<br />
              수신거부 시 서비스를 이용하실 수 없습니다.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowRejectConfirm(false)}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isSubmitting ? '처리 중...' : '수신거부'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 수신거부 성공 모달
  if (showRejectSuccess) {
    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">수신거부 완료</h3>
            <p className="text-gray-600 mb-6">
              수신거부가 완료되었습니다.<br />
              서비스를 이용하실 수 없습니다.
            </p>
            <button
              onClick={handleRejectSuccessConfirm}
              className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">약관 동의</h2>
            <p className="text-sm text-gray-600 mt-1">서비스 이용을 위해 다음 약관에 동의해주세요</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {/* 서비스 이용약관 (필수) */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <button
                  onClick={() => handleConsentChange('serviceTerms')}
                  className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consents.serviceTerms 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {consents.serviceTerms && <Check className="w-3 h-3 text-white" />}
                </button>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-red-500 font-medium">[필수]</span>
                    <span className="font-medium text-gray-900">서비스 이용약관</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    DW.Insights 서비스 이용에 필요한 기본 약관입니다.
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <button
                      onClick={() => toggleDetails('serviceTerms')}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                    >
                      <span>{showDetails.serviceTerms ? '간략히 보기' : '전문 보기'}</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <a
                      href="/terms/service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                    >
                      <span>새 창에서 보기</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  
                  {/* 서비스 이용약관 상세 내용 */}
                  {showDetails.serviceTerms && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <div className="text-sm text-gray-700 space-y-3 max-h-60 overflow-y-auto">
                        <div>
                          <h4 className="font-semibold mb-2">제1조 (목적)</h4>
                          <p>본 약관은 아이뱅크 디지털웍스(이하 &quot;회사&quot;)가 제공하는 DW.Insights 서비스(이하 &quot;서비스&quot;)의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제2조 (정의)</h4>
                          <p>본 약관에서 사용하는 용어의 정의는 다음과 같습니다:</p>
                          <ul className="list-disc pl-4 mt-1 space-y-1">
                            <li>&quot;서비스&quot;란 회사가 제공하는 DW.Insights 리포팅 서비스를 의미합니다.</li>
                            <li>&quot;이용자&quot;란 서비스에 접속하여 본 약관에 따라 서비스를 이용하는 회원 및 비회원을 의미합니다.</li>
                            <li>&quot;회원&quot;이란 회사에 개인정보를 제공하여 회원등록을 한 자로서, 회사의 정보를 지속적으로 제공받으며 서비스를 계속적으로 이용할 수 있는 자를 의미합니다.</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제3조 (약관의 효력 및 변경)</h4>
                          <p>본 약관은 서비스 화면에 게시하거나 기타의 방법으로 이용자에게 공지함으로써 효력이 발생합니다. 회사는 합리적인 사유가 발생할 경우에는 본 약관을 변경할 수 있으며, 약관이 변경되는 경우 변경된 약관의 내용과 시행일을 정하여, 시행일로부터 최소 7일 이전에 공지합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제4조 (서비스의 제공 및 변경)</h4>
                          <p>회사는 다음과 같은 업무를 수행합니다:</p>
                          <ul className="list-disc pl-4 mt-1 space-y-1">
                            <li>전문적 큐레이션을 통한 리포트 제공</li>
                            <li>검색 및 필터링 기능 제공</li>
                            <li>스크랩 및 관심사 관리 기능</li>
                            <li>기타 회사가 정하는 업무</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제5조 (회원가입)</h4>
                          <p>이용자는 회사가 정한 가입 양식에 따라 회원정보를 기입한 후 이 약관에 동의한다는 의사표시를 함으로서 회원가입을 신청합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제6조 (회원 탈퇴 및 자격 상실 등)</h4>
                          <p>회원은 회사에 언제든지 탈퇴를 요청할 수 있으며 회사는 즉시 회원탈퇴를 처리합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제7조 (개인정보보호)</h4>
                          <p>회사는 이용자의 개인정보 수집시 서비스제공을 위하여 필요한 범위에서 최소한의 개인정보를 수집합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제8조 (회사의 의무)</h4>
                          <p>회사는 법령과 본 약관이 금지하거나 공서양속에 반하는 행위를 하지 않으며 본 약관이 정하는 바에 따라 지속적이고, 안정적으로 서비스를 제공하는데 최선을 다하여야 합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제9조 (이용자의 의무)</h4>
                          <p>이용자는 다음 행위를 하여서는 안 됩니다:</p>
                          <ul className="list-disc pl-4 mt-1 space-y-1">
                            <li>신청 또는 변경시 허위 내용의 등록</li>
                            <li>타인의 정보 도용</li>
                            <li>회사가 게시한 정보의 변경</li>
                            <li>회사 기타 제3자의 저작권 등 지적재산권에 대한 침해</li>
                            <li>외설 또는 폭력적인 메시지, 화상, 음성, 기타 공서양속에 반하는 정보를 서비스에 공개 또는 게시하는 행위</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제10조 (저작권의 귀속 및 이용제한)</h4>
                          <p>회사가 작성한 저작물에 대한 저작권 기타 지적재산권은 회사에 귀속합니다. 이용자는 서비스를 이용함으로써 얻은 정보 중 회사에게 지적재산권이 귀속된 정보를 회사의 사전 승낙 없이 복제, 송신, 출판, 배포, 방송 기타 방법에 의하여 영리목적으로 이용하거나 제3자에게 이용하게 하여서는 안됩니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제11조 (면책조항)</h4>
                          <p>회사는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제12조 (재판권 및 준거법)</h4>
                          <p>서비스 이용으로 발생한 분쟁에 대해 소송이 제기되는 경우 회사의 본사 소재지를 관할하는 법원을 전속 관할 법원으로 합니다.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 개인정보 처리방침 (필수) */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <button
                  onClick={() => handleConsentChange('privacyPolicy')}
                  className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consents.privacyPolicy 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {consents.privacyPolicy && <Check className="w-3 h-3 text-white" />}
                </button>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-red-500 font-medium">[필수]</span>
                    <span className="font-medium text-gray-900">개인정보 처리방침</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    개인정보 수집, 이용, 보관에 대한 정책입니다.
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <button
                      onClick={() => toggleDetails('privacyPolicy')}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                    >
                      <span>{showDetails.privacyPolicy ? '간략히 보기' : '전문 보기'}</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <a
                      href="/terms/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                    >
                      <span>새 창에서 보기</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  
                  {/* 개인정보 처리방침 상세 내용 */}
                  {showDetails.privacyPolicy && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <div className="text-sm text-gray-700 space-y-3 max-h-60 overflow-y-auto">
                        <div>
                          <h4 className="font-semibold mb-2">제1조 (개인정보의 처리목적)</h4>
                          <p>아이뱅크 디지털웍스(이하 &quot;회사&quot;)는 다음의 목적을 위하여 개인정보를 처리하고 있으며, 다음의 목적 이외의 용도로는 이용하지 않습니다.</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>회원 가입 및 관리: 회원제 서비스 이용에 따른 본인확인, 개인 식별, 불량회원의 부정 이용 방지와 비인가 사용 방지</li>
                            <li>서비스 제공: 서비스 제공, 맞춤 서비스 제공, 본인인증, 요금결제·정산</li>
                            <li>마케팅 및 광고에의 활용: 신규 서비스 개발 및 특화, 이벤트 등 광고성 정보 전달</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제2조 (개인정보의 처리 및 보유기간)</h4>
                          <p>회사는 정보주체로부터 개인정보를 수집할 때 동의받은 개인정보 보유·이용기간 또는 법령에 따른 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>회원가입 및 관리: 회원탈퇴 시까지</li>
                            <li>서비스 제공: 서비스 제공 완료 시까지</li>
                            <li>마케팅 및 광고에의 활용: 마케팅 수신 동의 철회 시까지</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제3조 (처리하는 개인정보의 항목)</h4>
                          <p>회사는 다음의 개인정보 항목을 처리하고 있습니다.</p>
                          <div className="mt-2">
                            <p className="font-medium">필수항목:</p>
                            <ul className="list-disc pl-4 space-y-1">
                              <li>이름, 이메일 주소, 비밀번호</li>
                              <li>서비스 이용기록, 접속로그, 쿠키, 접속 IP 정보</li>
                            </ul>
                            <p className="font-medium mt-2">선택항목:</p>
                            <ul className="list-disc pl-4 space-y-1">
                              <li>마케팅 정보 수신 동의 여부</li>
                              <li>개인화 서비스 이용을 위한 관심사 정보</li>
                            </ul>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제4조 (개인정보의 제3자 제공)</h4>
                          <p>회사는 정보주체의 개인정보를 제1조에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 개인정보 보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제5조 (개인정보 처리의 위탁)</h4>
                          <p>회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다.</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>클라우드 서비스 제공업체: 서버 운영 및 데이터 보관</li>
                            <li>이메일 발송 서비스: 회원가입, 비밀번호 재설정 등 시스템 이메일 발송</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제6조 (정보주체의 권리·의무 및 행사방법)</h4>
                          <p>정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>개인정보 처리정지 요구권</li>
                            <li>개인정보 열람요구권</li>
                            <li>개인정보 정정·삭제요구권</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제7조 (개인정보의 파기)</h4>
                          <p>회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제8조 (개인정보의 안전성 확보조치)</h4>
                          <p>회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>관리적 조치: 내부관리계획 수립·시행, 정기적 직원 교육 등</li>
                            <li>기술적 조치: 개인정보처리시스템 등의 접근권한 관리, 접근통제시스템 설치</li>
                            <li>물리적 조치: 전산실, 자료보관실 등의 접근통제</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제9조 (개인정보 보호책임자)</h4>
                          <p>회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 개인정보 보호책임자를 지정하고 있습니다.</p>
                          <div className="mt-2 p-3 bg-white rounded border">
                            <p className="font-medium">개인정보 보호책임자</p>
                            <p>성명: 개인정보보호책임자</p>
                            <p>소속: 아이뱅크 디지털웍스</p>
                            <p>연락처: privacy@dwinsights.com, 02-3397-0442</p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제10조 (권익침해 구제방법)</h4>
                          <p>정보주체는 아래의 기관에 대해 개인정보 침해신고를 할 수 있습니다.</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>개인정보 침해신고센터 (privacy.go.kr / 국번없이 182)</li>
                            <li>개인정보 분쟁조정위원회 (www.kopico.go.kr / 국번없이 1833-6972)</li>
                            <li>대검찰청 사이버범죄수사단 (www.spo.go.kr / 02-3480-3573)</li>
                            <li>경찰청 사이버테러대응센터 (www.netan.go.kr / 국번없이 182)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 마케팅 수신동의 (선택) */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <button
                  onClick={() => handleConsentChange('marketingConsent')}
                  className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consents.marketingConsent 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {consents.marketingConsent && <Check className="w-3 h-3 text-white" />}
                </button>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 font-medium">[선택]</span>
                    <span className="font-medium text-gray-900">마케팅 정보 수신동의</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    이벤트, 프로모션, 신규 서비스 등 마케팅 정보를 받으실 수 있습니다.
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <button
                      onClick={() => toggleDetails('marketingConsent')}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                    >
                      <span>{showDetails.marketingConsent ? '간략히 보기' : '전문 보기'}</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <a
                      href="/terms/marketing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                    >
                      <span>새 창에서 보기</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  
                  {/* 마케팅 수신동의 약관 상세 내용 */}
                  {showDetails.marketingConsent && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <div className="text-sm text-gray-700 space-y-3 max-h-60 overflow-y-auto">
                        <div>
                          <h4 className="font-semibold mb-2">제1조 (목적)</h4>
                          <p>본 약관은 아이뱅크 디지털웍스(이하 &quot;회사&quot;)가 제공하는 DW.Insights 서비스의 마케팅 정보 수신에 관한 이용자의 동의와 관련된 사항을 규정함을 목적으로 합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제2조 (마케팅 정보의 정의)</h4>
                          <p>본 약관에서 &quot;마케팅 정보&quot;라 함은 다음 각 호의 정보를 의미합니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>신규 서비스 출시, 업데이트, 기능 개선에 관한 정보</li>
                            <li>이벤트, 프로모션, 할인 혜택에 관한 정보</li>
                            <li>서비스 이용 통계 및 트렌드 분석 정보</li>
                            <li>업계 동향 및 관련 뉴스레터</li>
                            <li>개인화된 콘텐츠 추천 및 맞춤형 서비스 안내</li>
                            <li>기타 회사가 제공하는 유용한 정보</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제3조 (수신 동의)</h4>
                          <p>이용자는 회사의 마케팅 정보 수신에 대하여 자유롭게 동의하거나 거부할 수 있습니다. 마케팅 정보 수신 동의는 선택사항이며, 동의하지 않아도 서비스 이용에는 제한이 없습니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제4조 (마케팅 정보 제공 방법)</h4>
                          <p>회사는 다음과 같은 방법으로 마케팅 정보를 제공할 수 있습니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>이메일 (이용자가 제공한 이메일 주소)</li>
                            <li>서비스 내 알림 (웹사이트, 모바일 앱)</li>
                            <li>SMS (이용자가 제공한 휴대폰 번호, 별도 동의 시)</li>
                            <li>푸시 알림 (모바일 앱 이용 시)</li>
                            <li>기타 이용자가 동의한 방법</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제5조 (마케팅 정보 제공 주기)</h4>
                          <p>마케팅 정보는 다음 기준에 따라 제공됩니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>신규 서비스 및 중요 업데이트: 즉시 또는 중요한 시점에 제공</li>
                            <li>일반 마케팅 정보: 주 1-2회 이내</li>
                            <li>이벤트 및 프로모션: 해당 이벤트 기간 중 적절한 시점에 제공</li>
                            <li>뉴스레터: 월 1-2회 정기 발송</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제6조 (개인정보 처리)</h4>
                          <p>마케팅 정보 제공을 위해 수집·이용되는 개인정보는 다음과 같습니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>필수정보: 이메일 주소</li>
                            <li>선택정보: 이름, 관심사, 서비스 이용 기록</li>
                          </ul>
                          <p className="mt-2">수집된 개인정보는 마케팅 정보 제공 목적으로만 이용되며, 관련 법령에 따라 보관·관리됩니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제7조 (동의 철회)</h4>
                          <p>이용자는 언제든지 마케팅 정보 수신 동의를 철회할 수 있으며, 동의 철회 시에는 다음과 같이 처리됩니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>마케팅 정보 제공이 즉시 중단됩니다</li>
                            <li>마케팅 목적의 개인정보 처리가 중단됩니다</li>
                            <li>동의 철회 후에도 서비스 이용에는 제한이 없습니다</li>
                            <li>동의 철회 시점 이후 마케팅 정보는 제공되지 않습니다</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제8조 (동의 철회 방법)</h4>
                          <p>마케팅 정보 수신 동의를 철회하는 방법은 다음과 같습니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>서비스 내 마이페이지에서 동의 설정 변경</li>
                            <li>수신된 마케팅 정보 하단의 &apos;수신거부&apos; 링크 클릭</li>
                            <li>고객센터 이메일 또는 전화를 통한 요청</li>
                            <li>회사가 제공하는 기타 방법</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제9조 (개인화된 마케팅)</h4>
                          <p>회사는 이용자의 서비스 이용 패턴, 관심사, 선호도 등을 분석하여 개인화된 마케팅 정보를 제공할 수 있습니다.</p>
                          <p className="mt-2">개인화된 마케팅을 위해 다음과 같은 정보가 활용될 수 있습니다:</p>
                          <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li>서비스 이용 기록 및 패턴</li>
                            <li>검색 키워드 및 관심 주제</li>
                            <li>스크랩 및 북마크 정보</li>
                            <li>서비스 내 활동 기록</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제10조 (제3자 정보 공유)</h4>
                          <p>회사는 마케팅 정보 제공을 위해 수집된 개인정보를 제3자와 공유하지 않으며, 마케팅 정보는 회사에서 직접 제공합니다. 단, 법령에 의해 요구되는 경우나 이용자의 명시적 동의가 있는 경우는 예외로 합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제11조 (정보의 정확성)</h4>
                          <p>회사는 제공하는 마케팅 정보의 정확성과 신뢰성을 위해 최선을 다하지만, 정보의 완전성이나 정확성을 보장하지는 않습니다. 이용자는 제공받은 정보를 참고용으로만 활용하시기 바랍니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제12조 (약관의 변경)</h4>
                          <p>본 약관은 필요에 따라 변경될 수 있으며, 변경 시에는 변경된 약관의 내용과 시행일을 정하여, 시행일로부터 최소 7일 이전에 공지합니다. 단, 중요한 내용의 변경인 경우에는 30일 이전에 공지합니다.</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">제13조 (분쟁 해결)</h4>
                          <p>마케팅 정보 수신과 관련하여 분쟁이 발생한 경우, 회사와 이용자는 성실히 협의하여 해결하도록 노력합니다. 협의로 해결되지 않는 경우 관련 법령 및 관할 법원의 규정에 따릅니다.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 안내 메시지 */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">개인정보 수집 및 이용 안내</p>
                <p>
                  필수 약관에 동의하지 않으면 서비스 이용이 제한될 수 있습니다. 
                  마케팅 정보 수신동의는 선택사항이며, 동의하지 않아도 서비스 이용에는 제한이 없습니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50 gap-3 sm:gap-0">
          <div className="text-sm text-gray-600 whitespace-nowrap">
            <span className="font-medium">필수 항목</span>에 모두 동의해주세요
          </div>
          <div className="flex space-x-3 w-full sm:w-auto">
            <button
              onClick={handleRejectConsent}
              disabled={isSubmitting}
              className="flex-1 sm:flex-none px-3 py-2 sm:px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? '처리 중...' : '수신거부'}
            </button>
            <button
              onClick={handleAgree}
              disabled={!isRequiredConsentsComplete || isSubmitting}
              className="flex-1 sm:flex-none px-4 py-2 sm:px-6 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? '처리 중...' : '동의하고 계속하기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
